<template>
  <div class="h-60 md:h-72 relative">
    <div
      :class="{ 'white-header': !hasPhotos }"
      class="header absolute w-full h-14 flex items-center justify-between px-4 z-10">
      <span v-if="secretToken && !isAuthenticated"/>

      <LinkBack
        v-else
        :path="isUserFeedEnabled ? '/creators' : '/'"
        :class="[hasPhotos ? 'text-white' : 'text-gray-900']"/>

      <h3
        :class="[hasPhotos ? 'text-white' : 'text-gray-900']"
        class="text-center flex-1 flex justify-center items-center">
        @{{ creator.username }}

        <InlineSvg
          v-if="!options.verificationEnabled && creator.tikTokFollowerCount"
          :src="require('@/assets/svg/design/verified.svg')"
          class="ml-1.5"/>
      </h3>

      <div class="flex items-center gap-3.5">
        <button
          v-if="isAbleToShare"
          type="button"
          class="btn p-0"
          @click="shareProfile">
          <InlineSvg
            class="text-white hover:text-gray-300 w-5"
            :src="require('@/assets/svg/design/share2.svg')"/>
        </button>

        <AppReportDots
          :source="'profile'"
          :dots-class="'text-white hover:text-gray-300'"
          :update-user="() => userGet({ username: creator.username })"
          :user="creator"/>
      </div>
    </div>

    <div class="relative h-full">
      <img
        v-if="creator.photos.items.length === 1"
        :src="creator.photos.items[0].links.r640x960"
        class="w-full h-60 md:h-72 object-top object-cover"
        :title="creator.name.trim()"
        :alt="creator.name ? creator.name.trim() + '\'s photo' : `User's photo`">

      <img
        v-else-if="creator.photos.items.length === 0"
        :src="`/svg/${creator.sex === 1 ? 'user' : 'woman'}-placeholder.svg`"
        class="w-full h-72 object-bottom object-contain"
        :title="creator.name.trim()"
        :alt="`${creator.sex === 1 ? 'Man' : 'Woman'}`">

      <div
        v-else-if="creator.photos.items.length > 1"
        class="h-full">
        <CreatorPhotosSwiper
          v-if="creator.photos.items.length > 1 && (options.multipleProfilePhotosEnabled || beforeAuthOptions.multipleProfilePhotosEnabled)"
          :items="creator.photos.items"/>

        <img
          v-else
          :src="creator.photos.items[0].links.r640x960"
          class="w-full h-60 md:h-72 object-top object-cover"
          :title="creator.name.trim()"
          :alt="creator.name ? creator.name.trim() + '\'s photo' : `User's photo`">
      </div>

      <Tops
        v-if="0 && options.topPayersEnabled"
        class="absolute left-4 bottom-4"/>
    </div>

    <div
      v-if="iconByCatalog.has(creator.creatorCategory) && options?.activeCreatorCategories"
      class="shadow shadow-gray-800 rounded-full bg-white h-7 px-2 absolute z-10 left-4 bottom-4 flex gap-1.5 items-center justify-center">
      <img
        :src="iconByCatalog.get(creator.creatorCategory)"
        class="w-3.5 h-3.5"
        alt="emoji">

      <span class="text-sm capitalize">{{ creator.creatorCategory }}</span>
    </div>
  </div>

  <Modal
    v-slot="{ values, closeModal }"
    ref="blockModal"
    name="BlockUser">
    <BlockUserContent
      :values="values"
      :heading="$t('creator.block.block_user')"
      @close="closeModal()"/>
  </Modal>

  <Modal
    v-slot="{ values, closeModal }"
    ref="reportModal"
    name="ReportUser">
    <BlockUserContent
      :heading="$t('creator.block.report_user')"
      :values="values"
      type="report"
      @close="closeModal()"/>
  </Modal>
</template>

<script>
import LinkBack from '@/components/Functional/LinkBack'
import Modal from '@/components/Functional/Modal'
import BlockUserContent from '@/components/ModalContents/BlockUserContent'
import { mapState, mapWritableState, mapActions } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useCreatorStore } from '@/stores/creator'
import helpers from '@/helpers'
import Tops from '@/components/Tops/Tops'
import CreatorPhotosSwiper from '@/components/Creator/Includes/CreatorPhotosSwiper.vue'
import uniqid from 'uniqid'
import { useAppStore } from '@/stores/app.js'
import AppReportDots from '@/components/App/AppReportDots.vue'

export default {
  name: 'CreatorHeader',
  components: {
    LinkBack,
    Modal,
    BlockUserContent,
    Tops,
    CreatorPhotosSwiper,
    AppReportDots
  },
  data () {
    return {
      shareData: {},
      iconByCatalog: new Map([
        ['new', require('@/assets/images/emoji/high-voltage.png')],
        ['top', require('@/assets/images/emoji/star.png')],
        ['hot', require('@/assets/images/emoji/fire.png')],
        ['all', false]
      ])
    }
  },
  computed: {
    ...mapState(useAppStore, [
      'secretToken'
    ]),
    ...mapState(useAuthStore, [
      'isAuthenticated',
      'isTokenExist',
      'isUserFeedEnabled',
      'options',
      'beforeAuthOptions'
    ]),
    ...mapWritableState(useCreatorStore, [
      'creator'
    ]),
    ...mapWritableState(useUserStore, [
      'options'
    ]),
    ...mapWritableState(useAuthStore, [
      'signupIsVisible'
    ]),
    hasPhotos () {
      return this.creator.photos.items.length
    },
    isAbleToShare () {
      return navigator.canShare
    }
  },
  mounted () {
    this.shareData = {
      title: 'Profile on One2Fan',
      text: 'Have a look at the profile on One2Fan',
      url: `https://one2fan.com/@${this.creator.username}`
    }
  },
  methods: {
    ...mapActions(useCreatorStore, [
      'userShared',
      'userGet'
    ]),
    async shareProfile () {
      if (!this.isAbleToShare) {
        this.$notify({
          id: uniqid(),
          group: 'errors',
          title: this.$t('common.warn'),
          text: this.$t('notifications.no_sharing_support')
        })

        return
      }

      await navigator.share(this.shareData).finally(() => {
        this.userShared(this.creator.userId)
      })
    },
    showOnbordingOrSignUp () {
      helpers.showOnbordingOrSignUp()
    }
    // blockCreator () {
    //   if (this.isTokenExist) {
    //     if (!this.creator.userId) {
    //       return
    //     }
    //
    //     this.$refs.blockModal.openModal({ userId: this.creator.userId })
    //   } else {
    //     helpers.showOnbordingOrSignUp()
    //   }
    // },
    // reportCreator () {
    //   if (this.isTokenExist) {
    //     if (!this.creator.userId) {
    //       return
    //     }
    //
    //     openReportModal({
    //       userId: this.creator.userId,
    //       source: 'profile'
    //     })
    //   } else {
    //     helpers.showOnbordingOrSignUp()
    //   }
    // }
  }
}
</script>

<style scoped>
  .header {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) -3.59%, rgba(0, 0, 0, 0) 100%);
  }
  .white-header {
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) -3.59%, rgba(0, 0, 0, 0) 100%);
  }
</style>
